import { components, paths } from '@types';

export enum MFA_TYPE {
  APP = 'app',
  SMS = 'sms',
}

//BEGIN: Table types
export type SORT_ORDER = 'asc' | 'desc';

export interface IColumnDefinition {
  id: string;
  label: string;
  sortable: boolean;
  width?: number;
  align?: 'left' | 'center' | 'right';
}

export type IPatientGroup =
  paths[`/reporting/patients/reports/{report_id}/groups`]['get']['responses']['200']['content']['application/json']['groups'][0];

export type IUserGroup =
  paths[`/reporting/users/reports/{report_id}/groups`]['get']['responses']['200']['content']['application/json']['groups'][0];

export type IRecordGroup =
  paths[`/reporting/record/reports/{report_id}/groups`]['get']['responses']['200']['content']['application/json']['groups'][0];

export type ITaskGroup =
  paths[`/reporting/tasks/reports/{report_id}/groups`]['get']['responses']['200']['content']['application/json']['groups'][0];

// Union type for all groups ( by group_by ) for reporting entities
export type ITableGroup =
  IPatientGroup | IUserGroup | IRecordGroup | ITaskGroup;

//END: Table types

//BEGIN: Report types
export type IPatientReport =
  paths['/reporting/patients/reports/{report_id}']['get']['responses']['200']['content']['application/json'];

export type ITaskReport =
  paths['/reporting/tasks/reports/{report_id}']['get']['responses']['200']['content']['application/json'];

export type IUserReport =
  paths['/reporting/users/reports/{report_id}']['get']['responses']['200']['content']['application/json'];

export type IRecordReport =
  paths['/reporting/record/reports/{report_id}']['get']['responses']['200']['content']['application/json'];

export type IReport =
  | (IPatientReport & { organization_id: string })
  | (ITaskReport & { organization_id: string })
  | (IUserReport & { organization_id: string })
  | ((IRecordReport & { organization_id: string }) & {
      organization_id: string;
    });
export type IReportIntrospection =
  paths['/reporting/reports/introspection']['get']['responses']['200']['content']['application/json'];

export enum ReportType {
  PATIENTS = 'patients_report',
  TASKS = 'tasks_report',
  USERS = 'users_report',
  RECORDS = 'records_report',
}

export enum ReportPatientReportType {
  ALL_PATIENTS = 'all_patients',
  MY_PATIENTS = 'my_patients',
  MY_TEAMS_PATIENTS = 'my_teams_patients',
}

export type ReportFilterEntryIntrospection =
  paths['/reporting/reports/introspection']['get']['responses']['200']['content']['application/json']['filters'][0];

export type ReportFilterEntry = {
  field: string;
  label: string;
  operator: string;
  value: string | string[];
  showValue?: string;
};

export enum ReportTimeInterval {
  ALL_TIME = 'all_time',
  CUSTOM = 'custom',
  LAST_YEAR = 'last_year',
  THIS_YEAR = 'this_year',
  LAST_QUARTER = 'last_quarter',
  THIS_QUARTER = 'this_quarter',
  LAST_MONTH = 'last_month',
  THIS_MONTH = 'this_month',
  CURRENT_AND_PREVIOUS_MONTH = 'current_and_previous_month',
  LAST_WEEK = 'last_week',
  THIS_WEEK = 'this_week',
  YESTERDAY = 'yesterday',
  TODAY = 'today',
  LAST_7_DAYS = 'last_7_days',
  LAST_30_DAYS = 'last_30_days',
  LAST_60_DAYS = 'last_60_days',
  LAST_90_DAYS = 'last_90_days',
  LAST_120_DAYS = 'last_120_days',
}

//END: report types

//BEGIN: User types, while it's not ready at the backend

export enum UserLandingPageENUM {
  DASHBOARD = 'DASHBOARD',
  WORKLIST = 'WORKLIST',
  ADMIN_PANEL = 'ADMIN_PANEL',
  REPORTS = 'REPORTS',
  DATA_ANALYSIS = 'DATA_ANALYSIS',
  PATIENTS = 'PATIENTS',
}

export interface IUser {
  email: string;
  exp: number;
  first_name: string;
  iat: number;
  id: string;
  last_name: string;
  permissions: string[];
  roles: string[];
  suffix: string | null;
  team: {
    id: string;
    name: string;
  };
  landing_page: UserLandingPageENUM;
}

export type ChangeProgramStatus =
  components['schemas']['ChangeProgramStatusRequest']['new_status'];

export type UnalignmentReason =
  components['schemas']['ChangeProgramStatusRequest']['unalignment_reason'];

export type UserStatus = components['schemas']['UserResponse']['status'];

export type PatientAccountStatus =
  components['schemas']['FilteredListPatient']['account_status'];
export const PATIENT_ACCOUNT_STATUSES = [
  'Active',
  'Inactive',
  'Ineligible',
] as const satisfies PatientAccountStatus[];

export type PatientGuideStatus =
  components['schemas']['FilteredListPatient']['guide_status'];

export enum USER_SUFFIX {
  APP = 'APP',
  ARNP = 'ARNP',
  CEMT = 'CEMT',
  CMA = 'CMA',
  CNA = 'CNA',
  CRT = 'CRT',
  DO = 'DO',
  DNP = 'DNP',
  EMT = 'EMT',
  FNP = 'FNP',
  LCSW = 'LCSW',
  MA = 'MA',
  MD = 'MD',
  NP = 'NP',
  PA = 'PA',
  'PA-C' = 'PA-C',
}

export enum LICENSE_TYPE {
  APP = 'APP',
  ARNP = 'ARNP',
  CEMT = 'CEMT',
  CMA = 'CMA',
  CNA = 'CNA',
  CRT = 'CRT',
  DO = 'DO',
  DNP = 'DNP',
  EMT = 'EMT',
  FNP = 'FNP',
  LCSW = 'LCSW',
  MA = 'MA',
  MD = 'MD',
  NP = 'NP',
  PA = 'PA',
  PA_C = 'PA-C',
}

export enum GENDER_TYPE {
  MALE = 'Male',
  FEMALE = 'Female',
  UNKNOWN = 'Unknown',
}

//END: User types

//BEGIN: Caregiver types
export enum RACE_TYPE {
  AMERICAN_INDIAN_OR_ALASKA_NATIVE = 'American Indian or Alaska Native',
  ASIAN = 'Asian',
  BLACK_OR_AFRICAN_AMERICAN = 'Black or African American',
  NATIVE_HAWAIIAN_OR_OTHER_PACIFIC_ISLANDER = 'Native Hawaiian or Other Pacific Islander',
  WHITE = 'White',
  ASKED_BUT_UNKNOWN = 'Asked but unknown',
  OTHER = 'Other',
  UNKNOWN = 'Unknown',
}

export enum ETHNICITY_TYPE {
  HISPANIC_OR_LATINO = 'Hispanic or Latino',
  NOT_HISPANIC_OR_LATINO = 'Not Hispanic or Latino',
  ASKED_BUT_UNKNOWN = 'Asked but unknown',
  UNKNOWN = 'Unknown',
}

export enum RELATIONSHIP_TYPE {
  SPOUSE = 'Spouse',
  DOMESTIC_PARTNER = 'Domestic partner',
  DAUGHTER = 'Daughter',
  SON = 'Son',
  SIBLING = 'Sibling',
  OTHER_FAMILY_MEMBER = 'Other family member',
  FRIEND = 'Friend',
  OTHER_NON_FAMILY_MEMBER = 'Other non-family member',
}

export enum WILLINGNESS_TYPE {
  WILLING = 'willing to participate in the GUIDE program',
  NOT_WILLING = 'not willing to participate in the GUIDE program',
  NOT_AVAILABLE = 'not available',
}

//END: Caregiver types

//BEGIN: Team types
export interface IUserTable {
  email: string;
  first_name: string;
  id: string;
  last_name: string;
  lead: boolean;
  role: string;
  suffix: string;
}

export interface ITeamTableEntry {
  id: string;
  name: string;
  description: string;
  color: string;
  city: string;
  state: string;
  country: string;
  managing_entity: { name: string; type: string };
  other_assignments: { name: string; type: string }[];
  lead_user: string;
  user_count: number;
  patient_count: number;
}

export type ITeamUser =
  paths['/team/{team_id}']['get']['responses']['200']['content']['application/json']['users'][number];

export interface ITeamDrawer {
  id: string;
  name: string;
  city: string;
  state: string;
  color: string;
  country: string;
  organization: {
    id: string;
    name: string;
  };
  managing_partner: {
    id: string;
    name: string;
  };
  records: Record[];
  users: User[];
  lead_user: string;
  user_count: number;
  patient_count: number;
}

interface Record {
  partner: Partner;
  practice?: Practice;
}

interface Partner {
  id: string;
  name: string;
}

interface Practice {
  id: string;
  name: string;
}

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  suffix: string;
  lead: boolean;
}

//END: Team types

//BEGIN: Partner types
export enum EMR_TYPE {
  ALLSCRIPTS = 'allscripts',
  APRIMA = 'aprima',
  ATHENA = 'athena',
  CENTRICITY = 'centricity',
  ECLINICALWORKS = 'eclinicalworks',
  EMDS = 'emds',
  EPIC = 'epic',
  GREENWAY_INTERGY = 'greenway_intergy',
  GREENWAY_PRIMESUIT = 'greenway_primesuite',
  HEALTHFUSION = 'healthfusion',
  NEXTGEN = 'nextgen',
  PRACTICEFUSION = 'practicefusion',
  TEBRA = 'tebra',
  OTHER = 'other',
}

//END: Partner types

//BEGIN: Task types
export enum TASK_PRIORITY {
  URGENT = 'Urgent',
  HIGH = 'High',
  MEDIUM = 'Medium',
  LOW = 'Low',
  OVERDUE = 'Overdue',
  DONE = 'Done',
}

export enum TASK_RECURRENCE {
  ONE_TIME = -1,
  EVERY_7_DAYS = 7,
  EVERY_10_DAYS = 10,
  EVERY_14_DAYS = 14,
  EVERY_21_DAYS = 21,
  EVERY_30_DAYS = 30,
  EVERY_90_DAYS = 90,
  EVERY_180_DAYS = 180,
  EVERY_365_DAYS = 365,
}

export type TASK_STATUS =
  paths[`/task/{task_id}`]['get']['responses']['200']['content']['application/json']['status'];
//END: Task types

//BEGIN: Form types
export type DynamicFormFieldTypes =
  components['schemas']['RequirementResponse']['type'];
//END: Form types

export type ProviderSpecialtyType =
  | 'Cardiology'
  | 'Palliative care'
  | 'Primary care'
  | 'Neurology'
  | 'Home Health'
  | 'Geropsych'
  | 'Social Worker';

export type License = components['schemas']['UserProviderLicensePayload'];
export type LicensesTypes =
  components['schemas']['UserProviderLicensePayload']['type'];
export const LicensesUnits = [
  'APP',
  'ARNP',
  'CEMT',
  'CMA',
  'CNA',
  'CRT',
  'DO',
  'DNP',
  'EMT',
  'FNP',
  'LCSW',
  'MA',
  'MD',
  'NP',
  'PA',
  'PA-C',
] as const satisfies LicensesTypes[];

export type BedUnits = components['schemas']['BuildingUnitBeds']['unit'];
export const BED_UNITS = [
  'assisted_living',
  'memory_care',
  'senior_living',
  'independent_living',
] as const satisfies BedUnits[];

export type Option = {
  value: string;
  label: string;
};

export type DownloadsReportEventsType =
  | 'report-download.progress'
  | 'report-download.updated'
  | 'report-download.completed'
  | 'care-plan-download.progress'
  | 'care-plan-download.updated'
  | 'care-plan-download.completed';

export enum ProgramName {
  GUIDE = 'GUIDE',
  PHARMACY = 'Pharmacy',
  MEMORY_CONNECT = 'MC',
  PCP = 'PCP',
  PRN = 'PRN',
  MOVE_IN_REPORT = 'Move-in Report',
  CCM = 'CCM',
  CARDIOLOGY = 'Cardiology',
  HOME_HEALTH = 'Home Health',
  PALLIATIVE = 'Palliative',
  BHI = 'BHI',
  HOSPICE = 'Hospice',
  HOME_CARE = 'Home Care',
  PART_B = 'Part B',
  PSYCH = 'Psych',
  TCM = 'TCM',
  RPM = 'RPM',
  SNF = 'SNF Resident',
  NEUROLOGY = 'Neurology',
  ONE_TIME = 'One-Time',
  WELLNESS = 'Wellness',
  WELLNESS_AT_RISK = 'Wellness (At risk)',
  LOST_PATIENT = 'Lost Patient',
  PARTNER = 'Partner',
  CONCIERGE = 'Concierge',
  HN_ACO_REACH = 'HN ACO REACH',
  CARE_AT_HOME = 'Care at Home',
  CARE_MORE = 'CareMore',
  OPTUM_MA = 'Optum MA',
  OPTUM_CARE_AT_HOME = 'Optum Care at Home',
}

export type PatientPreferences =
  paths[`/preferences/patient-table`]['get']['responses']['200']['content']['application/json'];

export type TaskPreferences =
  paths[`/preferences/task-table`]['get']['responses']['200']['content']['application/json'];

//Using a specific endpoint for the generic type here with the assumption that they are all the same
export type ColumnDensity = PatientPreferences['density'];
export type ColumnPreferencesConfig = components['schemas']['ColumnConfig'];

export interface DocumentFile {
  id: string;
  file_name: string;
  size: string;
  file_format: string;
}

export type NOTE_DECISION_TYPE = 'accepted' | 'edited' | 'declined';
